import { useCallback, useState, useEffect } from 'react';
import { Tooltip } from 'antd';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components';
import { SvgIcon } from '@/components/SvgIcon';

import styles from './DirectIndirectInputs.module.sass';
import {
  getValueWithEmpty,
  parseInputNumber,
} from './DirectIndirectInputs.helpers';

export type DirectIndirectInputsValue = {
  direct?: number;
  indirect?: number;
};

interface DirectIndirectInputsProps {
  value: DirectIndirectInputsValue;
  onChange: (value: DirectIndirectInputsValue) => void;
}

const DirectIndirectInputs = ({
  value,
  onChange,
}: DirectIndirectInputsProps) => {
  const { t } = useTranslation();
  const [isConnected, setIsConnected] = useState(false);

  const handleChangeDirect = useCallback(
    (newValue: string) => {
      onChange({
        ...value,
        direct: parseInputNumber(newValue),
      });
    },
    [onChange, value, isConnected],
  );

  const handleChangeIndirect = useCallback(
    (newValue: string) => {
      if (isConnected) return;
      onChange({
        ...value,
        indirect: parseInputNumber(newValue),
      });
    },
    [onChange, value, isConnected],
  );

  const handleToggleConnect = useCallback(() => {
    setIsConnected(!isConnected);
  }, [isConnected, onChange, value]);

  useEffect(() => {
    if (isConnected && value.indirect !== value.direct) {
      onChange({
        ...value,
        indirect: value.direct,
      });
    }
  }, [isConnected, value.direct]);

  const classes = cx(styles.inputs, {
    [styles['is-active']]: isConnected,
  });

  return (
    <div className={classes}>
      <div className={styles.field}>
        <div className={styles.label}>{t('translation.direct_connection')}</div>
        <div className={styles.value}>
          <Input
            id="direct"
            type="text"
            value={getValueWithEmpty(value.direct)}
            name="direct"
            className={styles.input}
            onChange={handleChangeDirect}
          />
          <SvgIcon name="dollar" className={styles.dollar} />
        </div>
      </div>

      <Tooltip
        zIndex={3001}
        title={
          isConnected
            ? t('translation.unlink_values')
            : t('translation.link_values')
        }
      >
        <button
          tabIndex={-1}
          type="button"
          className={styles.connect}
          onClick={handleToggleConnect}
        >
          <SvgIcon name="connect" />
        </button>
      </Tooltip>

      <div className={styles.field}>
        <div className={styles.label}>
          {t('translation.indirect_connection')}
        </div>
        <div className={styles.value}>
          <Input
            id="indirect"
            type="text"
            value={getValueWithEmpty(
              isConnected ? value.direct : value.indirect,
            )}
            name="indirect"
            disabled={isConnected}
            className={styles.input}
            onChange={handleChangeIndirect}
          />
          <SvgIcon name="dollar" className={styles.dollar} />
        </div>
      </div>
    </div>
  );
};

export default DirectIndirectInputs;
